<template>
  <v-form
    ref="sensor"
    v-model="isValidationOk"
    lazy-validation>
    <v-container
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        wrap>
        <v-flex
          xs12
          lg1
          md1>
          <v-text-field
            v-model.number="sensor.sensorNumber"
            :label="`${ $t('Number') }`"
            :count="3"
            :disabled="!isNewSensor"
            mask="###"
            class="dover-input"
            @input="onIdNumberChange()" />
        </v-flex>
        <v-flex
          xs12
          md1
          lg1>
          <v-switch
            v-model="sensor.enabled"
            :label="`${ $t('Enabled') }`"
            class="dover-input text-xs-center" />

        </v-flex>
        <v-flex
          xs6
          md6>
          <v-select
            v-model="sensor.sensorType"
            :items="sensorTypes"
            :label="`${ $t('Sensor type') }`"
            :disabled="!isNewSensor"
            item-text="desc"
            item-value="code"
            class="dover-input"
            chips>
            <template
              slot="selection"
              slot-scope="data">
              <v-chip
                :selected="data.selected"
                :disabled="data.disabled"
                :key="data.item.code"
                class="v-chip--select-multi "
                @input="data.parent.selectItem(data.item)">
                <v-avatar class="dover-avatar ">{{ data.item.code }}</v-avatar>
                {{ data.item.desc }}
              </v-chip>
            </template>
          </v-select>
        </v-flex>
        <v-flex
          xs12
          lg2
          md2>
          <v-text-field
            v-model.trim="sensor.sensorName"
            :rules="rules.required.concat(rules.maxLen_30)"
            :counter="30"
            :label="`${ $t('Name') }`"
            class="dover-input"/>
        </v-flex>

        <v-flex
          xs12
          md1
          lg1>
          <v-switch
            v-model="sensor.compliance"
            :label="`${ $t('Compliance') }`"
            class="dover-input text-xs-center" />
        </v-flex>

      </v-layout>
    </v-container>
    <v-container
      v-if="sensor.sensorType === 0"
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        row
        wrap>
        <v-flex
          xs3
          md3
          lg3>
          <v-select
            v-model="sensor.physicalChannels[0].address"
            :items="availablePositions"
            :label="`${ $t('Position') }`"
            item-text=""
            item-value=""
            class="dover-input"
            outline
            dense />
          <v-snackbar
            v-model="snackbarPosition"
            :timeout="snackbarTimeout"
            color="error" >
            {{ msgPositionAlreadyInUse }}
            <v-btn
              dark
              flat
              @click="snackbarPosition = false" >
              {{ $t('Close') }}
            </v-btn>
          </v-snackbar>
        </v-flex>
        <v-flex
          xs3
          md3
          lg3>
          <span
            class="caption grey--text font-weight-bold">
            {{ $t('Sensor Channel') }}
          </span>
          <br>
          <v-btn-toggle
            v-model="sensor.physicalChannels[0].device"
            dense
            group
            mandatory
            @change="onSensorChannelChange()">
            <v-btn
              v-for="(item) in sensorChannels"
              :key="item.id"
              :value="item.id"
              text
              small
              color="dover-blue">
              {{ item.desc }}
            </v-btn>
          </v-btn-toggle>
        </v-flex>
        <v-flex
          xs3
          md3
          lg3>
          <span
            class="caption grey--text font-weight-bold">
            {{ $t('Alarm status') }}
          </span>
          <br>
          <v-btn-toggle
            v-model="sensor.physicalChannels[0].normalClosed"
            dense
            group
            mandatory>
            <v-btn
              v-for="(item) in alarmStatus"
              :key="item.id"
              :value="item.id"
              text
              small
              color="dover-blue">
              {{ item.desc }}
            </v-btn>
          </v-btn-toggle>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container
      v-else-if="sensor.sensorType === 1"
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        row
        wrap>
        <v-flex
          xs3
          md3
          lg3>
          <v-select
            v-model="sensor.physicalChannels[0].address"
            :items="availablePositions"
            :label="`${ $t('Position of product') }`"
            item-text=""
            item-value=""
            class="dover-input"
            outline
            dense />
          <v-snackbar
            v-model="snackbarPosition"
            :timeout="snackbarTimeout"
            color="error" >
            {{ msgPositionAlreadyInUse }}
            <v-btn
              dark
              flat
              @click="snackbarPosition = false" >
              {{ $t('Close') }}
            </v-btn>
          </v-snackbar>
        </v-flex>
        <v-flex
          xs3
          md3
          lg3>
          <v-select
            v-model="sensor.physicalChannels[1].address"
            :items="availablePositions"
            :label="`${ $t('Position of water') }`"
            item-text=""
            item-value=""
            class="dover-input"
            outline
            dense />
          <v-snackbar
            v-model="snackbarPosition"
            :timeout="snackbarTimeout"
            color="error" >
            {{ msgPositionAlreadyInUse }}
            <v-btn
              dark
              flat
              @click="snackbarPosition = false" >
              {{ $t('Close') }}
            </v-btn>
          </v-snackbar>
        </v-flex>
        <v-flex
          xs3
          md3
          lg3>
          <span
            class="caption grey--text font-weight-bold">
            {{ $t('Sensor Channel') }}
          </span>
          <br>
          <v-btn-toggle
            v-model="sensor.physicalChannels[0].device"
            dense
            group
            mandatory
            @change="onSensorChannelChange()">
            <v-btn
              v-for="(item) in sensorChannels"
              :key="item.id"
              :value="item.id"
              text
              small
              color="dover-blue">
              {{ item.desc }}
            </v-btn>
          </v-btn-toggle>
        </v-flex>
        <v-flex
          xs3
          md3
          lg3>
          <span
            class="caption grey--text font-weight-bold">
            {{ $t('Alarm status') }}
          </span>
          <br>
          <v-btn-toggle
            v-model="sensor.physicalChannels[0].normalClosed"
            dense
            group
            mandatory>
            <v-btn
              v-for="(item) in alarmStatus"
              :key="item.id"
              :value="item.id"
              text
              small
              color="dover-blue">
              {{ item.desc }}
            </v-btn>
          </v-btn-toggle>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container
      v-else-if="sensor.sensorType === 3"
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        row
        wrap >
        <v-flex
          xs3
          md3
          lg3>
          <v-text-field
            v-model.number="sensor.physicalChannels[0].address"
            :rules="rules.required.concat(rules.maxLen_5).concat(rules.uniqueSerial)"
            :counter="5"
            :label="`${ $t('Serial Number') }`"
            :disabled="!isNewSensor"
            mask="#####"
            class="dover-input"
            @input="onSerialNumberChange()"/>
        </v-flex>
        <v-flex
          xs3
          md3
          lg3>
          <v-text-field
            v-model.number="sensor.thresholds[0].value"
            :rules="rules.required.concat(rules.level)"
            :counter="6"
            :label="`${ $t('Product threshold') + ' (' + [ options.levelUnits[systemSettings.levelUnit || 0].desc ] + ')' }`"
            class="dover-input"/>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container
      v-else-if="sensor.sensorType === 2"
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        row
        wrap >
        <v-flex
          xs3
          md3
          lg3>
          <v-text-field
            v-model.number="sensor.physicalChannels[0].address"
            :rules="rules.required.concat(rules.maxLen_5).concat(rules.uniqueSerial)"
            :counter="5"
            :label="`${ $t('Serial Number') }`"
            :disabled="!isNewSensor"
            mask="#####"
            class="dover-input"
            @input="onSerialNumberChange()"/>
        </v-flex>
        <v-flex
          xs3
          md3
          lg3>
          <v-text-field
            v-model.number="sensor.thresholds[0].value"
            :rules="rules.required.concat(rules.level)"
            :counter="6"
            :label="`${ $t('Product threshold') + ' (' + [ options.levelUnits[systemSettings.levelUnit || 0].desc ] + ')' }`"
            class="dover-input"/>
        </v-flex>
        <v-flex
          xs3
          md3
          lg3>
          <v-text-field
            v-model.number="sensor.thresholds[1].value"
            :rules="rules.required.concat(rules.level)"
            :counter="6"
            :label="`${ $t('Water threshold') + ' (' + [ options.levelUnits[systemSettings.levelUnit || 0].desc ] + ')' }`"
            class="dover-input"/>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container
      v-else
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        row
        wrap >
        <v-flex
          xs3
          md3
          lg3>
          <v-text-field
            v-model.number="sensor.physicalChannels[0].address"
            :rules="rules.required.concat(rules.maxLen_10).concat(rules.uniqueSerial)"
            :counter="10"
            :label="`${ $t('Serial Number') }`"
            :disabled="!isNewSensor"
            mask="###########"
            class="dover-input"
            @input="onSerialNumberChange"/>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        row
        wrap>
        <v-flex
          xs4
          md4
          lg4>
          <v-select
            v-model.number="locationIndex"
            :items="locations"
            :label="`${ $t('Location') }`"
            item-text="desc"
            item-value="index"
            class="dover-input"
            outline
            dense />
        </v-flex>
      </v-layout>
    </v-container>
  </v-form>
</template>

<script>
import Vue from 'vue'

export default {
  props: {
    isNewSensor: {
      type: Boolean,
      require: true,
      default: () => false
    },
    options: {
      type: Object,
      required: true,
      default: () => {}
    },
    sensor: {
      type: Object,
      required: true,
      default: () => {}
    },
    sensors: {
      type: Object,
      required: true,
      default: () => {}
    },
    sensorTypes: {
      type: Object,
      required: true,
      default: () => {}
    },
    siteInfo: {
      type: Object,
      required: true,
      default: () => {}
    },
    systemSettings: {
      type: Object,
      required: true,
      default: () => {}
    }
  },
  data: () => ({
    alarmStatus: [],
    availablePositions: [],
    isSensorValidated: false,
    locations: [],
    locationIndex: 0,
    maxDevices: 1,
    msgPositionAlreadyInUse: '',
    sensorChannels: [],
    snackbarPosition: false,
    snackbarTimeout: 0,
    rules: {
      level: [ v => Vue.prototype.Maglink.RulesRange(v, -500, 500) ],
      maxLen_5: [ v => Vue.prototype.Maglink.RulesRange(v, 1, 99999) ],
      maxLen_10: [ v => Vue.prototype.Maglink.RulesRange(v, 1, 9999999999) ],
      maxLen_30: [ v => Vue.prototype.Maglink.RulesMaxLen(v, 30) ],
      range: [ v => Vue.prototype.Maglink.RulesRange(v, 1, 256) ],
      required: [ v => Vue.prototype.Maglink.RulesRequired(v) ],
      uniqueId: [ v => true ],
      uniqueSerial: [ v => true ]
    },
    enumAssociationType: {
      tank: 0,
      sensor: 1,
      sump: 2, // dispenser, sump
      site: 3 // site, well
    }
  }),
  watch: {
    locationIndex: function () {
      let location = this.locations[this.locationIndex]

      this.sensor.associationType = location.associationType
      this.sensor.associationAddress = location.id
    },
    sensor: {
      handler: function (newSensor, oldSensor) {
        if (oldSensor !== undefined &&
              (newSensor.associationType === oldSensor.associationType && newSensor.associationAddress === oldSensor.associationAddress)) {
          return
        }
        this.setLocationIndex()
      },
      immediate: true
    },
    siteInfo: {
      handler: function (newSiteInfo, oldSiteInfo) {
        if (newSiteInfo === undefined) {
          return
        }

        this.getLocations()
        this.setLocationIndex()
      },
      immediate: true
    },
    systemSettings: {
      handler: function (newSystemSettings, oldSystemSettings) {
        this.applySettings()
      },
      immediate: true
    }
  },
  mounted () {
    this.getAlarmStatus()
    this.getSensorChannels()
    this.onSensorChannelChange()
  },
  methods: {
    applySettings () {
    },
    getAlarmStatus () {
      this.alarmStatus = []
      this.alarmStatus.push({ id: true, desc: this.$t('Open') })
      this.alarmStatus.push({ id: false, desc: this.$t('Closed') })
    },
    getLocations () {
      let totalTanks = this.siteInfo.tanksNumber
      let list = []
      for (let index = 1; index <= totalTanks; index++) {
        list.push({ index: list.length, id: index, desc: this.$t('Tank {0}', [index]), associationType: this.enumAssociationType.tank })
      }
      for (let index = 1; index <= totalTanks; index++) {
        list.push({ index: list.length, id: index, desc: this.$t('Sump {0}', [index]), associationType: this.enumAssociationType.sump })
      }
      list.push({ index: list.length, id: 1, desc: this.$t('Site/well'), associationType: this.enumAssociationType.site })
      this.locations = list
    },
    getSensorChannels () {
      this.sensorChannels = []
      if (!Vue.prototype.Maglink.isPlus) {
        this.sensorChannels.push({ id: 0, desc: this.$t('Internal') })
      }
      this.sensorChannels.push({ id: 1, desc: this.$t('Maglink I/O') })
    },
    setLocationIndex () {
      if (this.sensor === undefined || this.locations.length === 0) {
        return
      }

      for (let i = 0; i < this.locations.length; i++) {
        let location = this.locations[i]

        if (location.associationType === this.sensor.associationType && location.id === this.sensor.associationAddress) {
          this.locationIndex = location.index
          return
        }
      }
    },
    onIdNumberChange () {
      if (!this.isNewSensor) {
        return
      }

      let sensorNumber = this.sensor.sensorNumber * 1
      let uniqueRule = [ v => true ]

      for (let index = 0; index < this.sensors.length; index++) {
        let item = this.sensors[index]

        if (item.sensorNumber * 1 === sensorNumber) {
          uniqueRule = [ this.$t('Sensor number "{0}" already configured', [ sensorNumber ]) ]
          this.snack('error', this.$t('Sensor number "{0}" already configured', [ sensorNumber ]))
          this.sensor.sensorNumber = 0
          break
        }
      }

      this.rules.uniqueId = uniqueRule
    },
    onSensorChannelChange () {
      if (this.sensor.sensorType > 1) {
        return
      }

      let item = this.sensor
      let totalItems = (item.physicalChannels[0].device === 0) ? 6 : 32

      let list = []
      for (let index = 1; index <= totalItems; index++) {
        list.push(index)
      }

      for (let devIndex = 0; devIndex < this.maxDevices; devIndex++) {
        if (item.physicalChannels[devIndex].address > totalItems) {
          item.physicalChannels[devIndex].address = 1
        }
      }

      this.availablePositions = list
    },
    onSensorTypeChange () {
      if (this.sensor === undefined) {
        return
      }

      switch (this.sensor.sensorType) {
        case 0:
          this.maxDevices = 1
          break

        default:
          break
      }
    },
    onSerialNumberChange () {
      let sensorAddress = this.sensor.physicalChannels[0].address
      let sensorNumber = this.sensor.sensorNumber
      let uniqueRule = [ v => true ]

      for (let index = 0; index < this.sensors.length; index++) {
        let item = this.sensors[index]

        if (item.sensorNumber === sensorNumber) { continue }

        if (item.physicalChannels[0].address === sensorAddress) {
          uniqueRule = [ this.$t('Sensor number "{0}" already configured', [ sensorNumber ]) ]
          this.snack('error', this.$t('Sensor number "{0}" already configured', [ sensorNumber ]))
          this.sensor.physicalChannels[0].address = 0
          break
        }
      }

      this.rules.uniqueSerial = uniqueRule
    },
    snack (color, message) {
      message = this.$t('Sensors setup') + ': ' + message
      this.$store.dispatch('showSnackbar', { color, message, show: true })
    }
  }
}
</script>
